import queryString, { ParseOptions } from 'query-string';
import mapValues from 'lodash/mapValues';

export type QueryParamSpec =
    | 'boolean'
    | 'boolean[]'
    | 'integer'
    | 'integer[]'
    | 'number'
    | 'number[]'
    | 'string'
    | 'string[]';

export type QueryParamsSpec = {
    readonly [name: string]: QueryParamSpec;
};

export type QueryParamValueType<S extends QueryParamSpec> = S extends 'boolean'
    ? boolean
    : S extends 'boolean[]'
    ? ReadonlyArray<boolean>
    : S extends 'integer'
    ? number | undefined
    : S extends 'integer[]'
    ? ReadonlyArray<number>
    : S extends 'number'
    ? number | undefined
    : S extends 'number[]'
    ? ReadonlyArray<number>
    : S extends 'string'
    ? string | undefined
    : S extends 'string[]'
    ? ReadonlyArray<string>
    : unknown;

export type ParsedQueryParams<S extends QueryParamsSpec> = {
    readonly [K in keyof S]: QueryParamValueType<S[K]>;
};

type Parser<P extends QueryParamSpec> = (params: string | string[] | undefined | null) => QueryParamValueType<P>;

const parseBooleanParam: Parser<'boolean'> = params => {
    const param = Array.isArray(params) ? params[0] : params;
    return param?.toLowerCase() === 'true';
};

const parseBooleanArrayParam: Parser<'boolean[]'> = params => {
    if (!params) {
        return [];
    }
    return (Array.isArray(params) ? params : [params]).map(it => it?.toLowerCase() === 'true');
};

const parseIntegerParam: Parser<'integer'> = params => {
    if (!params) {
        return undefined;
    }
    const param = (Array.isArray(params) ? params[0] : params).trim();
    const num = param ? Math.floor(Number(param)) : NaN;
    return isNaN(num) ? undefined : num;
};

const parseIntegerArrayParam: Parser<'integer[]'> = params => {
    if (!params) {
        return [];
    }
    return (Array.isArray(params) ? params : [params])
        .map(it => it.trim())
        .filter(it => !!it)
        .map(it => Math.floor(Number(it)))
        .filter(it => !isNaN(it));
};

const parseNumberParam: Parser<'number'> = params => {
    if (!params) {
        return undefined;
    }
    const param = (Array.isArray(params) ? params[0] : params).trim();
    const num = param ? Number(param) : NaN;
    return isNaN(num) ? undefined : num;
};

const parseNumberArrayParam: Parser<'number[]'> = params => {
    if (!params) {
        return [];
    }
    return (Array.isArray(params) ? params : [params])
        .map(it => it.trim())
        .filter(it => !!it)
        .map(it => Number(it))
        .filter(it => !isNaN(it));
};

const parseStringParam: Parser<'string'> = params => {
    const param = Array.isArray(params) ? params[0] : params;
    return param ?? undefined;
};

const parseStringArrayParam: Parser<'string[]'> = params => {
    if (!params) {
        return [];
    }
    return Array.isArray(params) ? params : params ? [params] : [];
};

const PARSERS: { [P in QueryParamSpec]: Parser<P> } = {
    boolean: parseBooleanParam,
    'boolean[]': parseBooleanArrayParam,
    integer: parseIntegerParam,
    'integer[]': parseIntegerArrayParam,
    number: parseNumberParam,
    'number[]': parseNumberArrayParam,
    string: parseStringParam,
    'string[]': parseStringArrayParam
};

const PARSE_OPTIONS: ParseOptions = {
    parseBooleans: false,
    parseNumbers: false
};

export function parseQueryParams<S extends QueryParamsSpec>(search: string, options: S): ParsedQueryParams<S> {
    const parsedParams = queryString.parse(search, PARSE_OPTIONS);

    return mapValues(options, (option, name) => {
        const rawParamValue = parsedParams[name];
        const parser = PARSERS[option] ?? option;
        return parser(rawParamValue);
    });
}

export function serializeQueryParams(params: { [key: string]: any }): string {
    return queryString.stringify(params);
}

export function updateQueryParams(currentSearch: string, params: { [key: string]: any }): string {
    const currentParams = queryString.parse(currentSearch, PARSE_OPTIONS);
    return serializeQueryParams({ ...currentParams, ...params });
}
